import React from "react"
import { motion } from "framer-motion"
import  type {Variants} from "framer-motion"
import {
  Calendar,
  Loader2,
  FileText,
  FilePlus,
  FileCheck2,
  ShoppingBag,
  CreditCard,
  Hammer,
  Eye,
  Truck,
} from "lucide-react"

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.35,
      delayChildren: 0.1,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16, x: -8 },
  visible: {
    opacity: 1,
    y: 0,
    x: 0,
    transition: {
      duration: 0.45,
      ease: "easeOut",
    },
  },
}

const getLineVariants = (itemCount: number): Variants => ({
  hidden: { scaleY: 0 },
  visible: {
    scaleY: 1,
    transition: {
      delay: 0.1,
      duration: Math.max(0.45, (itemCount - 1) * 0.35 + 0.45),
      ease: "linear",
    },
  },
})

function formatDate(isoStr?: string | null) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function formatDateTime(isoStr?: string | null) {
  if (!isoStr) return "-"
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return "-"
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export interface CheckpointItem {
  type: string
  status: string
  timestamp: string
  description?: string
}

export interface RfqStatusUpdatesTimelineProps {
  isLoading?: boolean
  checkpoints?: CheckpointItem[]
  createdAt?: string | null
  validUntil?: string | null
  createdLabel?: string
}

export function RfqStatusUpdatesTimeline({
  isLoading = false,
  checkpoints = [],
  createdAt,
  validUntil,
  createdLabel = "SENT DATE",
}: RfqStatusUpdatesTimelineProps) {
  // Calculate validity difference
  const diffTime = validUntil ? new Date(validUntil).getTime() - new Date().getTime() : 0
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  const expirySubtext = validUntil
    ? diffDays > 0
      ? `Expires in ${diffDays} day${diffDays > 1 ? "s" : ""}`
      : "Expired"
    : null

  return (
    <div className="lg:col-span-1 space-y-4 lg:border-l lg:border-primary/10 lg:pl-4 text-left">
      <div className="space-y-3">
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
          <Calendar className="size-3.5 text-primary" /> Status Updates
        </h4>

        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-slate-400">
            <Loader2 className="size-4 animate-spin text-primary" />
            <span className="text-xs">Loading status updates...</span>
          </div>
        ) : checkpoints && checkpoints.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative pl-6 space-y-5 max-h-[500px] overflow-y-auto pr-2 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {/* Connecting Timeline Line */}
            <motion.div
              variants={getLineVariants(checkpoints.length)}
              style={{ originY: 0 }}
              className="absolute left-[8px] top-1.5 bottom-1.5 w-[1.5px] bg-primary/30"
            />

            {checkpoints.map((cp, idx) => {
              const title = cp.type.replace(/_/g, " ")
              let icon = <FileText className="size-3" />
              let iconColorClass = "text-sky-600"
              let bgColorClass = "bg-sky-50 border-sky-100"

              switch (cp.type) {
                case "RFQ_CREATED":
                  icon = <FilePlus className="size-3" />
                  iconColorClass = "text-blue-600"
                  bgColorClass = "bg-blue-50 border-blue-100"
                  break
                case "QUOTATION":
                  icon = <FileCheck2 className="size-3" />
                  iconColorClass = "text-violet-600"
                  bgColorClass = "bg-violet-50 border-violet-100"
                  break
                case "ORDER":
                  icon = <ShoppingBag className="size-3" />
                  iconColorClass = "text-indigo-600"
                  bgColorClass = "bg-indigo-50 border-indigo-100"
                  break
                case "PAYMENT":
                  icon = <CreditCard className="size-3" />
                  iconColorClass = "text-emerald-600"
                  bgColorClass = "bg-emerald-50 border-emerald-100"
                  break
                case "PRODUCTION_STAGE":
                  icon = <Hammer className="size-3" />
                  iconColorClass = "text-amber-600"
                  bgColorClass = "bg-amber-50 border-amber-100"
                  break
                case "INSPECTION":
                  icon = <Eye className="size-3" />
                  iconColorClass = "text-teal-600"
                  bgColorClass = "bg-teal-50 border-teal-100"
                  break
                case "SHIPMENT":
                  icon = <Truck className="size-3" />
                  iconColorClass = "text-rose-600"
                  bgColorClass = "bg-rose-50 border-rose-100"
                  break
              }

              return (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="relative flex gap-2.5 items-start"
                >
                  {/* Icon Circle */}
                  <div
                    className={`absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border ${bgColorClass} shadow-sm z-10 bg-white`}
                  >
                    <div className={`${iconColorClass}`}>{icon}</div>
                  </div>

                  {/* Checkpoint Details */}
                  <div className="space-y-1 min-w-0 flex-1">
                    <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wider block">
                      {formatDateTime(cp.timestamp)}
                    </span>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <h5 className="text-[11px] font-extrabold text-slate-800 leading-tight">
                        {title}
                      </h5>
                      <span className="rounded px-1.5 py-0.5 text-[8px] font-black bg-slate-100 text-slate-600 border border-slate-205/60 uppercase tracking-wider shrink-0">
                        {cp.status.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative pl-5 space-y-4"
          >
            {/* Connecting Timeline Line */}
            <motion.div
              variants={getLineVariants((createdAt ? 1 : 0) + (validUntil ? 1 : 0))}
              style={{ originY: 0 }}
              className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-primary/20"
            />

            {/* Created Date Point */}
            {createdAt && (
              <motion.div variants={itemVariants} className="relative flex gap-2.5 items-start">
                <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-sky-100 bg-[#e0f2fe]">
                  <div className="size-1.5 rounded-full bg-sky-600"></div>
                </div>
                <div className="space-y-0.5 min-w-0">
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                    {createdLabel}
                  </span>
                  <p className="text-xs font-bold text-slate-800 leading-none">
                    {formatDate(createdAt)}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Valid Until Point */}
            {validUntil && (
              <motion.div variants={itemVariants} className="relative flex gap-2.5 items-start">
                <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-red-100 bg-[#fee2e2]">
                  <div className="size-1.5 rounded-full bg-red-600"></div>
                </div>
                <div className="space-y-0.5 min-w-0">
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                    VALID UNTIL
                  </span>
                  <p className="text-xs font-bold text-slate-800 leading-none">
                    {formatDate(validUntil)}
                  </p>
                  {expirySubtext && (
                    <p
                      className={`text-[9px] font-semibold ${
                        diffDays > 0 ? "text-primary" : "text-slate-400"
                      }`}
                    >
                      {expirySubtext}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}